import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Send, CheckCircle, Users, Plus, X, Upload, FileText } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Phone number formatting utility
const formatPhoneNumber = (value) => {
  // Remove all non-digits
  const phoneNumber = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limitedNumber = phoneNumber.slice(0, 10);
  
  // Format as (XXX) XXX-XXXX
  if (limitedNumber.length === 0) return '';
  if (limitedNumber.length <= 3) return `(${limitedNumber}`;
  if (limitedNumber.length <= 6) return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3)}`;
  return `(${limitedNumber.slice(0, 3)}) ${limitedNumber.slice(3, 6)}-${limitedNumber.slice(6)}`;
};

// Email validation utility
const isValidEmail = (email) => {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
};

const EmploymentApplicationPage = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    position: '',
    experience_years: '',
    start_date: '',
    desired_pay: '',
    previous_employer: '',
    previous_position: '',
    reason_for_leaving: '',
    skills: '',
    certifications: '',
    about_yourself: '',
    job_id: ''
  });
  
  // Separate state for references (repeatable)
  const [references, setReferences] = useState([
    { name: '', relationship: '', company: '', phone: '', email: '' }
  ]);
  
  // File upload state
  const [resumeFile, setResumeFile] = useState(null);
  const [additionalFiles, setAdditionalFiles] = useState([]);
  const [uploadingResume, setUploadingResume] = useState(false);
  const [uploadingAdditional, setUploadingAdditional] = useState(false);
  
  // Form state
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const positions = [
    'Project Manager', 'Site Supervisor', 'Lead Carpenter', 'Carpenter',
    'Carpenter Helper', 'General Laborer', 'Estimator', 'Office Administrator', 'Other'
  ];

  useEffect(() => {
    const jobId = searchParams.get('job');
    const position = searchParams.get('position');
    if (jobId || position) {
      setFormData(prev => ({
        ...prev,
        job_id: jobId || '',
        position: position || ''
      }));
    }
  }, [searchParams]);

  // Handle reference changes
  const handleReferenceChange = (index, field, value) => {
    const newReferences = [...references];
    if (field === 'phone') {
      newReferences[index][field] = formatPhoneNumber(value);
    } else {
      newReferences[index][field] = value;
    }
    setReferences(newReferences);
  };

  // Add new reference
  const addReference = () => {
    if (references.length < 5) {
      setReferences([...references, { name: '', relationship: '', company: '', phone: '', email: '' }]);
    }
  };

  // Remove reference
  const removeReference = (index) => {
    if (references.length > 1) {
      setReferences(references.filter((_, i) => i !== index));
    }
  };

  // Handle resume upload
  const handleResumeUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['.pdf', '.doc', '.docx', '.txt'];
    const fileExt = '.' + file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExt)) {
      toast.error('Invalid File Type', {
        description: 'Please upload a PDF, DOC, DOCX, or TXT file.',
      });
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', {
        description: 'File size must be less than 5MB.',
      });
      return;
    }
    
    setUploadingResume(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/employment/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setResumeFile({
        filename: response.data.filename,
        original_name: response.data.original_name,
        url: response.data.url
      });
    } catch (error) {
      console.error('Error uploading resume:', error);
      toast.error('Upload Failed', {
        description: 'Failed to upload resume. Please try again.',
      });
    } finally {
      setUploadingResume(false);
    }
  };

  // Handle additional file upload
  const handleAdditionalUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (additionalFiles.length >= 3) {
      toast.error('Maximum Files Reached', {
        description: 'Maximum 3 additional files allowed.',
      });
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File Too Large', {
        description: 'File size must be less than 5MB.',
      });
      return;
    }
    
    setUploadingAdditional(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${BACKEND_URL}/api/employment/upload`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAdditionalFiles([...additionalFiles, {
        filename: response.data.filename,
        original_name: response.data.original_name,
        url: response.data.url
      }]);
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Upload Failed', {
        description: 'Failed to upload file. Please try again.',
      });
    } finally {
      setUploadingAdditional(false);
    }
  };

  // Remove additional file
  const removeAdditionalFile = (index) => {
    setAdditionalFiles(additionalFiles.filter((_, i) => i !== index));
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    // Email validation
    if (!isValidEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    // Phone validation (must have 10 digits)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (phoneDigits.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }
    
    // Reference email validation
    references.forEach((ref, index) => {
      if (ref.email && !isValidEmail(ref.email)) {
        newErrors[`ref_email_${index}`] = 'Please enter a valid email';
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Filter out empty references
      const validReferences = references.filter(ref => ref.name.trim() !== '');
      
      const submitData = {
        ...formData,
        references: validReferences.length > 0 ? validReferences : null,
        resume_file: resumeFile?.url || null,
        additional_files: additionalFiles.length > 0 ? additionalFiles.map(f => f.url) : null
      };
      
      await axios.post(`${BACKEND_URL}/api/employment`, submitData);
      
      // Increment job application count if applying for specific job
      if (formData.job_id) {
        try {
          await axios.post(`${BACKEND_URL}/api/jobs/${formData.job_id}/increment-applications`);
        } catch (err) {
          console.log('Could not increment job count');
        }
      }
      
      setIsSubmitted(true);
      
      // Show success toast
      toast.success('Application Submitted Successfully!', {
        description: 'Thank you for applying to join our team. We will review your application and contact you soon.',
        duration: 5000,
      });
    } catch (error) {
      console.error('Error submitting application:', error);
      toast.error('Submission Failed', {
        description: 'There was an error submitting your application. Please try again.',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Apply phone formatting
    if (name === 'phone') {
      setFormData({ ...formData, [name]: formatPhoneNumber(value) });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-52 pb-16 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-1/2 w-96 h-96 bg-cyan-500 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="w-16 h-16 bg-cyan-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-cyan-400" />
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
            Employment Application
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Join our team of dedicated professionals. We're looking for talented individuals who are passionate about quality craftsmanship and building lasting relationships.
          </p>
        </div>
      </section>

      {/* Application Form Section */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2">Application Received!</h3>
                <p className="text-slate-600">
                  Thank you for your interest in joining C3 Contracting. We'll review your application and contact you if there's a potential fit.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="text-center mb-8">
                  <h2 className="text-2xl font-bold text-slate-900 mb-2">Personal Information</h2>
                  <p className="text-slate-600">Please fill out the form below to apply for a position.</p>
                </div>

                {/* Personal Details */}
                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">First Name *</Label>
                    <Input name="first_name" value={formData.first_name} onChange={handleChange} required className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Last Name *</Label>
                    <Input name="last_name" value={formData.last_name} onChange={handleChange} required className="h-12" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Email *</Label>
                    <Input 
                      name="email" 
                      type="email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required 
                      className={`h-12 ${errors.email ? 'border-red-500' : ''}`}
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Phone *</Label>
                    <Input 
                      name="phone" 
                      type="tel" 
                      value={formData.phone} 
                      onChange={handleChange} 
                      required 
                      className={`h-12 ${errors.phone ? 'border-red-500' : ''}`}
                      maxLength={14}
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Street Address *</Label>
                  <Input name="address" value={formData.address} onChange={handleChange} required className="h-12" />
                </div>

                <div className="grid sm:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">City *</Label>
                    <Input name="city" value={formData.city} onChange={handleChange} required className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">State *</Label>
                    <Input name="state" value={formData.state} onChange={handleChange} required className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">ZIP Code *</Label>
                    <Input name="zip_code" value={formData.zip_code} onChange={handleChange} required className="h-12" />
                  </div>
                </div>

                {/* Position Information */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Position Information</h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Position Applying For *</Label>
                    <Select value={formData.position} onValueChange={(v) => setFormData({ ...formData, position: v })}>
                      <SelectTrigger className="h-12"><SelectValue placeholder="Select a position" /></SelectTrigger>
                      <SelectContent>
                        {positions.map((pos) => (<SelectItem key={pos} value={pos}>{pos}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Years of Experience *</Label>
                    <Input name="experience_years" type="number" value={formData.experience_years} onChange={handleChange} required className="h-12" min="0" />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Available Start Date *</Label>
                    <Input name="start_date" type="date" value={formData.start_date} onChange={handleChange} required className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Desired Pay (Hourly/Salary)</Label>
                    <Input name="desired_pay" value={formData.desired_pay} onChange={handleChange} className="h-12" />
                  </div>
                </div>

                {/* Previous Employment */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Previous Employment</h3>
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <Label className="text-slate-700 mb-2 block">Most Recent Employer</Label>
                    <Input name="previous_employer" value={formData.previous_employer} onChange={handleChange} className="h-12" />
                  </div>
                  <div>
                    <Label className="text-slate-700 mb-2 block">Position Held</Label>
                    <Input name="previous_position" value={formData.previous_position} onChange={handleChange} className="h-12" />
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Reason for Leaving</Label>
                  <Input name="reason_for_leaving" value={formData.reason_for_leaving} onChange={handleChange} className="h-12" />
                </div>

                {/* Skills & Qualifications */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Skills & Qualifications</h3>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Relevant Skills *</Label>
                  <Textarea name="skills" value={formData.skills} onChange={handleChange} required rows={3} />
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Certifications & Licenses</Label>
                  <Input name="certifications" value={formData.certifications} onChange={handleChange} className="h-12" />
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Tell Us About Yourself *</Label>
                  <Textarea name="about_yourself" value={formData.about_yourself} onChange={handleChange} required rows={4} />
                </div>

                {/* Resume Upload */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">Resume & Documents</h3>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Resume</Label>
                  <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-cyan-500 transition-colors">
                    {resumeFile ? (
                      <div className="flex items-center justify-center gap-3">
                        <FileText className="w-6 h-6 text-cyan-600" />
                        <span className="text-slate-700">{resumeFile.original_name}</span>
                        <button
                          type="button"
                          onClick={() => setResumeFile(null)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.txt"
                          onChange={handleResumeUpload}
                          className="hidden"
                          disabled={uploadingResume}
                        />
                        <div className="flex flex-col items-center gap-2">
                          <Upload className="w-8 h-8 text-slate-400" />
                          <span className="text-slate-600">
                            {uploadingResume ? 'Uploading...' : 'Click to upload resume'}
                          </span>
                          <span className="text-sm text-slate-400">PDF, DOC, DOCX, or TXT (max 5MB)</span>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                <div>
                  <Label className="text-slate-700 mb-2 block">Additional Documents (Certifications, etc.)</Label>
                  <div className="space-y-3">
                    {additionalFiles.map((file, index) => (
                      <div key={index} className="flex items-center gap-3 bg-slate-50 p-3 rounded-lg">
                        <FileText className="w-5 h-5 text-cyan-600" />
                        <span className="text-slate-700 flex-1">{file.original_name}</span>
                        <button
                          type="button"
                          onClick={() => removeAdditionalFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                    {additionalFiles.length < 3 && (
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          onChange={handleAdditionalUpload}
                          className="hidden"
                          disabled={uploadingAdditional}
                        />
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-cyan-500 transition-colors">
                          <span className="text-slate-600">
                            {uploadingAdditional ? 'Uploading...' : '+ Add additional document'}
                          </span>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Professional References */}
                <div className="border-t border-slate-200 pt-6 mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-slate-900">Professional References</h3>
                    {references.length < 5 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={addReference}
                        className="text-cyan-600 border-cyan-600 hover:bg-cyan-50"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Reference
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mb-4">Please provide 2-3 professional references.</p>
                </div>

                {references.map((ref, index) => (
                  <div key={index} className="bg-slate-50 p-4 rounded-lg space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-slate-700">Reference {index + 1}</span>
                      {references.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeReference(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      )}
                    </div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 mb-1 block text-sm">Name</Label>
                        <Input
                          value={ref.name}
                          onChange={(e) => handleReferenceChange(index, 'name', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 mb-1 block text-sm">Relationship</Label>
                        <Input
                          value={ref.relationship}
                          onChange={(e) => handleReferenceChange(index, 'relationship', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 mb-1 block text-sm">Company</Label>
                        <Input
                          value={ref.company}
                          onChange={(e) => handleReferenceChange(index, 'company', e.target.value)}
                          className="h-10"
                        />
                      </div>
                      <div>
                        <Label className="text-slate-600 mb-1 block text-sm">Phone</Label>
                        <Input
                          type="tel"
                          value={ref.phone}
                          onChange={(e) => handleReferenceChange(index, 'phone', e.target.value)}
                          className="h-10"
                          maxLength={14}
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label className="text-slate-600 mb-1 block text-sm">Email</Label>
                        <Input
                          type="email"
                          value={ref.email}
                          onChange={(e) => handleReferenceChange(index, 'email', e.target.value)}
                          className={`h-10 ${errors[`ref_email_${index}`] ? 'border-red-500' : ''}`}
                        />
                        {errors[`ref_email_${index}`] && (
                          <p className="text-red-500 text-sm mt-1">{errors[`ref_email_${index}`]}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold h-12">
                  {isSubmitting ? 'Submitting...' : <><Send className="mr-2 w-5 h-5" /> Submit Application</>}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EmploymentApplicationPage;
